import { closestCorners, DndContext, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { MyStoreBundle } from '@1po/1po-bff-fe-spec/generated/my_store/bundles/response/GetMyStoreBundlesResponse';
import { MyStoreReference } from '@1po/1po-bff-fe-spec/generated/my_store/references/response/GetMyStoreReferencesResponse';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { ColumnsType } from 'antd/es/table';
import { ROUTER_ESTIMATE } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { trackAppEvent } from 'app/AppTracker';
import { AddToEstimateButtonAndDialog } from 'components/AddToEstimate/AddToEstimateButtonAndDialog';
import { DataContainer } from 'components/DataContainer';
import { addBundleFromMyStore } from 'domains/estimate/Estimate.store';
import { useFetchMyStoreBundles } from 'domains/myStore/MyStore.requests';
import {
  addBundleRequestSaga,
  addBundlesSectionRequestSaga,
  getAllBundleCodes,
  getAllSelectedBundles,
  getBundlesSections,
  getIsBundleSelected,
  getIsSectionSelected,
  moveBundlesSectionRequestSaga,
  removeBundleRequestSaga,
  removeBundleSectionReuestSaga,
  renameBundlesSectionRequestSaga,
  setBundleSelection,
  updateBundleCodeRequestSaga,
  updateBundleDesignationRequestSaga,
  updateBundlePriceRequestSaga,
} from 'domains/myStore/MyStore.store';
import { BUNDLES } from 'domains/myStore/MyStore.types';
import { GetTitle } from 'pages/EstimatePage/TableSection/tableComponents';
import MyStoreHeader from 'pages/MyStorePage/MyStoreHeader';
import BundlesHeader from 'pages/MyStorePage/Pages/Bundles/BundlesHeader';
import BundlesSection, { SelectBundles } from 'pages/MyStorePage/Pages/Bundles/BundlesSection';
import { ValidatedPriceInput, ValidatedTextInput } from 'pages/MyStorePage/Pages/Bundles/BundlesTable/BundlesInput';
import { CenteredSpin, CenterFlex, Container, Flex, MarginBox, NotificationLink, notifyTop, TrashAltButton } from 'UI';
import { restrictToContainerId } from 'utils/dnd-utils';
import { TRACKING_EVENT_ADD_TO_ESTIMATE, TRACKING_EVENT_GO_TO_ESTIMATE } from 'utils/eventTracker/EventTracker.types';
import { scrollToTopSmooth, useResetScroll } from 'utils/hooks/useResetScroll';

export const Bundles = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const bundlesSections = useSelector(getBundlesSections);
  const allBundleCodes = useSelector(getAllBundleCodes);
  const selectedBundles = useSelector((state: RootState) => getAllSelectedBundles(state));
  const [isDraggingSectionId, setIsDraggingSectionId] = React.useState<string>('');

  useFetchMyStoreBundles();
  useResetScroll();

  const handleDragStart = (event: DragStartEvent) => {
    setIsDraggingSectionId(event.active.id as string);
  };

  const handleAddBundle = (sectionId: string) => {
    dispatch(addBundleRequestSaga({ sectionId }));
  };

  const handleAddSection = () => {
    dispatch(addBundlesSectionRequestSaga());
    scrollToTopSmooth();
  };

  const handleDeleteBundleSection = (sectionId: string) => {
    dispatch(removeBundleSectionReuestSaga({ sectionId }));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setIsDraggingSectionId('');
    dispatch(moveBundlesSectionRequestSaga(event));
  };

  const handleOnChangeSelection = (sectionId: string, isSelected: boolean, bundleId?: string) => {
    dispatch(setBundleSelection({ sectionId, isSelected, bundleId }));
  };

  const handleSectionTitleChange = (sectionId: string, title: string) => {
    dispatch(renameBundlesSectionRequestSaga({ sectionId, title }));
  };

  const handleAddToEstimateClick = (bundles: MyStoreBundle[], quantity?: number) => {
    notifyTop(
      'success',
      bundles.length > 1 ? (
        <Trans i18nKey={'estimate.bundles.addedMultipleToEstimate'}>
          {'Bundles have been added to your estimate.'}
        </Trans>
      ) : (
        <Trans i18nKey={'estimate.bundles.addedToEstimate'}>{'Bundle has been added to your estimate.'}</Trans>
      ),
      undefined,
      <NotificationLink
        onClick={() => {
          trackAppEvent(TRACKING_EVENT_GO_TO_ESTIMATE);
          history.push(`${ROUTER_ESTIMATE}`);
        }}
      >
        <Trans i18nKey={'catalog.reference_card.added_to_estimate.go_to_estimate'}>{'Go to estimate'}</Trans>
      </NotificationLink>,
    );
    trackAppEvent(TRACKING_EVENT_ADD_TO_ESTIMATE);
    dispatch(addBundleFromMyStore({ bundles, quantity }));
  };

  const isBundleCodeUnique = (newBundleCode: string, oldBundleCode: string) => {
    if (newBundleCode === oldBundleCode) {
      return true;
    }
    return !allBundleCodes.includes(newBundleCode);
  };

  function isBundleRowValid(row: MyStoreBundle): boolean {
    return !!row && !!row?.code && !!row?.price && !!row?.designation;
  }

  const getColumns = (sectionId: string) => [
    {
      title: (
        <CenterFlex>
          <SelectBundles
            entityId={sectionId}
            enabled={true}
            handleOnChange={(isSelected: boolean) => handleOnChangeSelection(sectionId, isSelected)}
            getIsSelectedSelector={getIsSectionSelected}
          />
        </CenterFlex>
      ),
      dataIndex: 'id',
      render: (bundleId: string, row: MyStoreBundle) => (
        <CenterFlex>
          <SelectBundles
            entityId={bundleId}
            enabled={isBundleRowValid(row)}
            handleOnChange={(isSelected: boolean) => handleOnChangeSelection(sectionId, isSelected, bundleId)}
            getIsSelectedSelector={getIsBundleSelected}
          />
        </CenterFlex>
      ),
      width: 48,
    },
    {
      title: GetTitle(t('estimate.bundles.bundle_code', 'Bundle code')),
      dataIndex: 'code',
      render: (code: string, row: MyStoreBundle) => (
        <ValidatedTextInput
          value={code}
          validate={(v) => v.length > 0 && isBundleCodeUnique(v, code)}
          saveFunction={(bundleCode) => {
            if (bundleCode !== code) {
              dispatch(updateBundleCodeRequestSaga({ sectionId, bundleId: row.id, bundleCode }));
            }
          }}
        />
      ),
      width: 300,
    },
    {
      title: GetTitle(t('common.designation', 'Designation')),
      dataIndex: 'designation',
      render: (designation: string, row: MyStoreBundle) => (
        <ValidatedTextInput
          value={designation}
          validate={(v) => v.length > 0}
          saveFunction={(bundleDesignation) => {
            if (bundleDesignation !== designation) {
              dispatch(
                updateBundleDesignationRequestSaga({
                  sectionId,
                  bundleId: row.id,
                  bundleDesignation,
                }),
              );
            }
          }}
        />
      ),
    },
    {
      title: GetTitle(t('common.price.VAT_excl_price', 'VAT Excl. Price')),
      dataIndex: 'price',
      render: (price: string, row: MyStoreBundle) => (
        <ValidatedPriceInput
          value={price}
          validate={(v) => v !== undefined && v != null && v?.toString()?.length > 0}
          saveFunction={(bundlePrice) => {
            if (bundlePrice && bundlePrice !== price) {
              dispatch(
                updateBundlePriceRequestSaga({
                  sectionId,
                  bundleId: row.id,
                  bundlePrice: Number(bundlePrice),
                }),
              );
            }
          }}
        />
      ),
      width: 170,
    },
    {
      title: '',
      dataIndex: 'id',
      render: (rowId: string) => (
        <CenterFlex>
          <TrashAltButton
            onClick={() => {
              dispatch(removeBundleRequestSaga({ sectionId, rowId }));
            }}
          />
        </CenterFlex>
      ),
      width: 48,
    },
    {
      title: '',
      render: (row: MyStoreBundle) => (
        <CenterFlex>
          <AddToEstimateButtonAndDialog
            buttonComponent={'icon'}
            disabled={!isBundleRowValid(row)}
            handleAddToEstimateClick={(quantity) => handleAddToEstimateClick([row], quantity)}
          />
        </CenterFlex>
      ),
      width: 48,
    },
  ];

  return (
    <Flex direction={'column'}>
      <DataContainer
        data={bundlesSections.sections.searchStatus}
        Loading={() => (
          <MarginBox mt={'10%'}>
            <CenteredSpin />
          </MarginBox>
        )}
      >
        <MyStoreHeader
          rightSection={
            <BundlesHeader
              myStoreType={BUNDLES}
              handleAddSection={handleAddSection}
              disabled={!selectedBundles?.length}
              handleAddToEstimateClick={(quantity) => handleAddToEstimateClick(selectedBundles, quantity)}
            />
          }
        />
        <Container>
          <div id={'dnd-container'}>
            <Flex direction={'column'} gap={15}>
              <DndContext
                collisionDetection={closestCorners}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
                modifiers={[restrictToVerticalAxis, restrictToContainerId('dnd-container')]}
              >
                <SortableContext items={bundlesSections.sections.data ?? []} strategy={verticalListSortingStrategy}>
                  {bundlesSections.sections.data?.map((bundlesSection, index) => (
                    <BundlesSection
                      key={bundlesSection.id}
                      sectionId={bundlesSection.id}
                      title={bundlesSection?.title ?? ''}
                      items={bundlesSection.bundles ?? []}
                      isDragging={bundlesSection.id === isDraggingSectionId}
                      initiallyOpen={index === 0}
                      myStoreType={BUNDLES}
                      handleAddItemInSection={() => handleAddBundle(bundlesSection.id)}
                      handleDeleteSection={() => handleDeleteBundleSection(bundlesSection.id)}
                      handleOnChangeSelection={handleOnChangeSelection}
                      handleSectionTitleChange={handleSectionTitleChange}
                      getIsSelectedSelector={getIsSectionSelected}
                      columns={getColumns(bundlesSection.id) as ColumnsType<MyStoreBundle | MyStoreReference>}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </Flex>
          </div>
        </Container>
      </DataContainer>
    </Flex>
  );
};
